<template>
    <page-select
        v-model="selected"
        :options="mergedOptions"
        :loading="loading"
        :getOptionKey="getOptionKey"
        @search="fetchAudiences"
        data-test="page-select-audiences"
    >
        <template v-slot:header>Choose Audiences</template>
        <template v-slot:subheader>You can choose to send to 1 or more audiences.</template>

        <template v-slot:select-all-right>
            <div class="grid grid-cols-[136px_100px] gap-4 items-center">
                <div class="text-sm">Source</div>
                <div class="text-sm text-right pr-4">Size</div>
            </div>
        </template>

        <template v-slot:before-items>
            <vx-alert
                v-if="hasMixedTypes"
                color="info"
                :closeable="false"
                class="mb-2 [&>div]:gap-2"
                data-test="mixing-warning"
            >
                <template v-slot:icon>
                    <font-awesome-icon :icon="faDiamondExclamation" />
                </template>
                <div class="flex justify-between items-center">
                    <span> Voxie audience sources cannot be mixed with external sources. </span>
                    <vx-button @click="resetSelection" size="sm" color="info" data-test="reset-selection">
                        Reset
                        <font-awesome-icon :icon="faRotateLeft" />
                    </vx-button>
                </div>
            </vx-alert>
        </template>

        <template v-slot:option="{ item }">
            <div class="grid grid-cols-[1fr_120px_100px] gap-4 items-center">
                <div :title="item.name">
                    <div v-if="!onCorporate() && item.is_template" class="flex gap-1">
                        <font-awesome-icon widthAuto :icon="faCrown" class="text-sm text-amber-400"></font-awesome-icon>
                        <span class="text-xs text-slate-700"> Corporate Segment </span>
                    </div>
                    <div class="font-bold line-clamp-1">{{ item.name }}</div>
                </div>

                <div class="text-sm">
                    {{ getSource(item) }}
                </div>

                <div class="text-sm text-right pr-4">
                    <template v-if="getContactCount(item) !== null && getContactCount(item) !== undefined">
                        {{ formatCount(getContactCount(item)) }}
                    </template>
                    <vx-icon-loading v-else class="w-4 h-4" spin />
                </div>
            </div>
        </template>
    </page-select>
</template>

<script setup lang="ts">
import { faCrown, faDiamondExclamation, faRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxIconLoading } from '@voxie/frontend-components';
import { computed, inject, onMounted, ref } from 'vue';
import PageSelect from '~/components/general/PageSelect.vue';
import { externalListVendor, formatCount } from '~/components/filters';
import { useSegmentsContactsCount } from '~/composables/useSegmentsContactsCount';
import { useExternalListsContactCount } from '~/composables/useExternalListsContactCount';
import { catchRequestError } from '~/utils/request';
import { useToasted } from '~/composables/useToasted';
import type { Features } from '~/provides/features.types';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { externalListsClient, segmentsClient } from '~/services';
import type { GetSegmentsParams } from '~/services/segments/segmentsClient.types';
import debounce from '~/utils/debounce';
import type { SegmentAudienceType } from '~/constants/segment';

export interface AudienceItem {
    id: number;
    name: string;
    label: string;
    sourceType: 'segment' | 'external-list';
    vendor?: string;
    disabled?: boolean;
    [key: string]: unknown;
}

const props = defineProps<{
    audienceType: SegmentAudienceType;
    groupIds?: string[];
    flowId?: string;
}>();

const selected = defineModel<AudienceItem[]>('modelValue', { required: true });

const { contactsCount: segmentContactsCount, fetchSegmentsContactsCount } = useSegmentsContactsCount();
const { contactsCount: externalListContactsCount, fetchExternalListsContactCount } = useExternalListsContactCount();

const loading = ref(false);
const segments = ref<AudienceItem[]>([]);
const externalLists = ref<AudienceItem[]>([]);
const error = ref('');

const toasted = useToasted();
const FEATURES = inject('FEATURES') as Features;

const { onCorporate, currentGroup, currentOrgUnit } = useCurrentGroup();

const teamId = Spark.state.currentTeam.id;

const selectedSourceType = computed(() => {
    return selected.value[0]?.sourceType;
});

const mergedOptions = computed(() => {
    const combined = [...segments.value, ...externalLists.value];
    return combined
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        .map((option) => {
            option.disabled = selectedSourceType.value && selectedSourceType.value !== option.sourceType;
            return option;
        });
});

const fetchAudiences = debounce(async (query?: string) => {
    loading.value = true;
    error.value = '';

    try {
        const context = {
            teamId,
            orgUnitId: currentOrgUnit.value?.id,
            groupId: currentGroup.value?.group_id,
        };

        const params: GetSegmentsParams = {
            per_page: 10,
            query,
            filter: {
                audience_type: props.audienceType,
                flow: FEATURES.flows
                    ? {
                          in_flow: props.flowId ? 1 : 0,
                          flow_id: props.flowId,
                      }
                    : undefined,
                is_template: onCorporate() ? 0 : undefined,
            },
        };

        const segmentsResponse = await segmentsClient.getSegments(context, params);

        segments.value = segmentsResponse.data.data.map((seg) => ({
            ...seg,
            sourceType: 'segment' as const,
            label: `${seg.id} - ${seg.name}`,
        })) as AudienceItem[];

        try {
            await fetchSegmentsContactsCount(
                context,
                segments.value.map((s) => s.id),
                { group_ids: props.groupIds }
            );
        } catch (e) {
            console.warn('Failed to fetch segment contact counts:', e);
        }

        if (FEATURES.external_contact_lists) {
            const externalListsResponse = await externalListsClient.getExternalLists(teamId, {
                per_page: 10,
            });

            externalLists.value = externalListsResponse.data.data.map(
                (list: { id: number; name: string; vendor: string }) => ({
                    ...list,
                    sourceType: 'external-list' as const,
                    label: `${list.id} - ${list.name}`,
                })
            ) as AudienceItem[];

            try {
                await fetchExternalListsContactCount(externalLists.value.map((l) => l.id));
            } catch (e) {
                console.warn('Failed to fetch external list contact counts:', e);
            }
        }
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        loading.value = false;
    }
}, 300);

const hasMixedTypes = computed(() => {
    if (selected.value.length === 0) {
        return false;
    }

    const types = new Set(selected.value.map((item) => item.sourceType));
    return types.size > 1;
});

const getOptionKey = (option: AudienceItem) => {
    return `${option.sourceType}_${option.id}`;
};

const getSource = (item: AudienceItem) => {
    if (item.sourceType === 'segment') {
        return 'Voxie';
    }
    return externalListVendor(item.vendor ?? null);
};

const getContactCount = (item: AudienceItem) => {
    if (item.sourceType === 'segment') {
        return segmentContactsCount.value[item.id];
    }

    return externalListContactsCount.value[item.id];
};

const resetSelection = () => {
    selected.value = [];
};

onMounted(() => {
    fetchAudiences();
});

defineExpose({
    hasMixedTypes,
});
</script>
